/**
 * 確認画面（プレビュー・エラー確認）の純ロジック（#4728）。
 *
 * マッピング結果を配送依頼項目の並びに変換し、画面側で判定できる形式チェック
 * （日付・数値・必須空欄・時間制限の組み合わせ）と、マスタ参照項目の存在チェックを行う。
 * マスタの候補（masterCandidates）は実マスタ検索 API の結果をコンテナ層
 * （useShipmentOrderCsvImport）が取得して渡す。候補が無い項目は存在チェックをしない。
 * 明細ID の重複やパートナーの特定など、画面側では判定できない検証は事前チェック API
 * （server-issues.ts の mergeServerCheck）で重ねる。
 */

import { SHIPMENT_ORDER_CSV_IMPORT } from '@/constants/common/message.constants'

import { type ColumnMapping } from './column-mapping'
import { type EffectiveTable } from './csv-import-state'
import { LOGI_FIELDS, type LogiField, type LogiFieldValue } from './logi-fields'

/** 日付として形式チェックする項目（registration-shape.ts の型変換でも同じ集合を使う） */
export const DATE_FIELDS: ReadonlySet<LogiFieldValue> = new Set([
  'shippingDate',
  'deliveryDate',
  'dispatchDate',
  'dispatchSlotDepartureAt',
  'dispatchSlotArrivalAt'
])

/** 数値として形式チェックする項目（registration-shape.ts の型変換でも同じ集合を使う） */
export const NUMBER_FIELDS: ReadonlySet<LogiFieldValue> = new Set([
  'income',
  'payment',
  'packagedLoadQuantity',
  'quantity',
  'weight'
])

/**
 * マスタ参照項目（コードの存在チェック対象）。
 * useMasterReferenceCandidates が取得する実マスタ検索APIのキーと一致させる
 * （一括入力候補と存在チェックは同じ実データを使う）。
 */
export const MASTER_REFERENCE_FIELDS: ReadonlySet<LogiFieldValue> = new Set([
  'customerId',
  'shipmentCategoryId',
  'originSiteId',
  'destSiteId',
  'partnerId',
  'truckModelId',
  'packageCategoryId'
])

export type MasterCandidate = { code: string; name: string }
export type MasterCandidatesByField = Partial<
  Record<LogiFieldValue, ReadonlyArray<MasterCandidate>>
>

/**
 * 値が候補コード一覧に含まれるか（前後の空白は除去して比較）。
 * candidates が undefined（該当項目の実データ未取得・取得中）の場合はチェックしない
 * ——読み込み中の項目を誤って「未登録コード」と判定しないため。
 */
const isKnownMasterCode = (
  field: LogiFieldValue,
  raw: string,
  masterCandidates: MasterCandidatesByField
): boolean => {
  const candidates = masterCandidates[field]
  if (!candidates) return true
  const value = raw.trim()
  return candidates.some((c) => c.code === value)
}

export type PreviewCellError = {
  field: LogiFieldValue
  reason: string
}

export type PreviewDataRow = {
  /** データ行番号（実効テーブル内 1 始まり） */
  rowNo: number
  cells: Partial<Record<LogiFieldValue, string>>
  errors: PreviewCellError[]
  /**
   * 項目を特定できない行の問題（事前チェック API が返す明細ID の重複・運行計画の不整合等）。
   * 画面側の形式チェックでは発生しない（常に空）。
   */
  rowErrors: string[]
  /** インライン修正済みの項目（修正後も再編集できるようにするための印） */
  editedFields: LogiFieldValue[]
}

/**
 * 確認画面でのインライン修正（rowNo → 項目 → 修正後の値）。
 * 「実行に使うのは常に画面の現在値」の原則をセル値にも適用する——
 * 修正は取り込みデータにのみ反映され、元ファイルは変更しない。
 */
export type CellOverrides = Record<
  number,
  Partial<Record<LogiFieldValue, string>>
>

export type PreviewResult = {
  /** マッピング済みの項目（カタログ表示順） */
  fields: LogiField[]
  rows: PreviewDataRow[]
  okCount: number
  errorCount: number
}

const DATE_PATTERN =
  /^\d{4}([/-])\d{1,2}\1\d{1,2}([ T]\d{1,2}:\d{2}(:\d{2})?)?$|^\d{8}$/

/** 年月日が実在する暦日か（うるう年を含む）。Date.parse は 2026/02/31 を 2026/03/03 に繰り上げて通すため別に確かめる */
const isExistingCalendarDate = (value: string): boolean => {
  const m = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/.exec(value)
  if (!m) return false
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1) return false
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return day <= daysInMonth
}

/**
 * 日付として読み取る（yyyy/MM/dd・yyyy-MM-dd・yyyyMMdd と ISO 日時を許容）。読み取れない・実在しない日付は null。
 * Date.parse 単独だと「2026」等の数値も日付扱いになるため、形式を絞る。
 */
export const toDate = (value: string): Date | null => {
  const v = value.trim()
  if (!DATE_PATTERN.test(v)) return null
  const normalized = /^\d{8}$/.test(v)
    ? `${v.slice(0, 4)}/${v.slice(4, 6)}/${v.slice(6, 8)}`
    : v
  if (!isExistingCalendarDate(normalized)) return null
  const t = Date.parse(normalized)
  return Number.isNaN(t) ? null : new Date(t)
}

const isParsableDate = (value: string): boolean => toDate(value) !== null

/** 比較用にパースする。読み取れない場合は null（呼び出し側は isParsableDate 済みの値のみ渡す想定）。 */
const parseComparableDate = toDate

/** 数値として読み取れるか（カンマ区切りは許容）。 */
const isParsableNumber = (value: string): boolean => {
  const v = value.trim().replace(/,/g, '')
  return v !== '' && !Number.isNaN(Number(v))
}

/**
 * 開始/終了が空欄側でも、行のエラー欄として user が実際に見て直せる項目に error を寄せる。
 * 空欄側が fields（＝マッピング済み or skippedFields）に無い場合（標準フォーマットで
 * 対応するCSV列が無い場合等）は、値がある側にエラーを立てる——存在しない列に
 * エラーを立てても確認画面のインライン修正では触れないため。
 */
const pushToVisibleField = (
  errors: PreviewCellError[],
  fields: readonly LogiField[],
  candidates: readonly LogiFieldValue[],
  reason: string
): void => {
  const visible = candidates.find((c) => fields.some((f) => f.value === c))
  errors.push({ field: visible ?? candidates[0], reason })
}

/**
 * 発地/着地の到着時間制限のクロスフィールド検証。
 * 出典: features/shipment-orders/models/create-shipment-order-create-form-value.ts の
 * custom() バリデータ（開始/終了ペア必須・終了は開始以降・着地開始は発地開始以降）。
 */
const checkTimeRestrictionConstraints = (
  record: Partial<Record<LogiFieldValue, string>>,
  fields: readonly LogiField[],
  errors: PreviewCellError[]
): void => {
  const checkPair = (
    startField: LogiFieldValue,
    endField: LogiFieldValue
  ): void => {
    const startRaw = record[startField]?.trim() ?? ''
    const endRaw = record[endField]?.trim() ?? ''
    const hasStart = startRaw !== ''
    const hasEnd = endRaw !== ''
    if (hasStart !== hasEnd) {
      // 空欄側を優先候補にしつつ、空欄側の列がそもそも fields に無い場合
      // （標準フォーマットで対応列自体が無い等）は、値がある側に寄せる。
      const emptyField = hasStart ? endField : startField
      const presentField = hasStart ? startField : endField
      pushToVisibleField(
        errors,
        fields,
        [emptyField, presentField],
        SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_TIME_RESTRICTION_PAIR_REQUIRED
      )
      return
    }
    if (!hasStart || !hasEnd) return
    const start = parseComparableDate(startRaw)
    const end = parseComparableDate(endRaw)
    if (start && end && end.getTime() < start.getTime()) {
      pushToVisibleField(
        errors,
        fields,
        [endField, startField],
        SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_TIME_RESTRICTION_ORDER_INVALID
      )
    }
  }

  checkPair('departureTimeRestrictionStart', 'departureTimeRestrictionEnd')
  checkPair('arrivalTimeRestrictionStart', 'arrivalTimeRestrictionEnd')

  const departureStartRaw = record.departureTimeRestrictionStart?.trim() ?? ''
  const arrivalStartRaw = record.arrivalTimeRestrictionStart?.trim() ?? ''
  if (departureStartRaw !== '' && arrivalStartRaw !== '') {
    const departureStart = parseComparableDate(departureStartRaw)
    const arrivalStart = parseComparableDate(arrivalStartRaw)
    if (
      departureStart &&
      arrivalStart &&
      arrivalStart.getTime() < departureStart.getTime()
    ) {
      pushToVisibleField(
        errors,
        fields,
        ['arrivalTimeRestrictionStart'],
        SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_ARRIVAL_BEFORE_DEPARTURE
      )
    }
  }
}

/**
 * 取り込めない行か（セルのエラーか、項目を特定できない行のエラーがある）。
 * 「エラー行を除外して取り込む」で外す行と、完了画面の登録内容に出さない行はこの判定でそろえる
 */
export const isErrorRow = (
  row: Pick<PreviewDataRow, 'errors' | 'rowErrors'>
): boolean => row.errors.length > 0 || row.rowErrors.length > 0

/** 取消の行の印（標準 CSV の任意列「取消」と同じ値） */
const REVOKE_MARK = '○'

/** 取消の行で形式を見る項目（BE の標準パーサーの取消の行の検証と同じ） */
const REVOKED_ROW_CHECKED_FIELDS: ReadonlySet<LogiFieldValue> = new Set([
  'externalKey',
  'orderNo',
  'shippingDate',
  'deliveryDate'
])

/**
 * マッピング結果から確認画面用のプレビューを組み立てる。
 * - fields: マッピング済み項目 + 「列が無いので後で手入力する」とマークされた項目
 *   （skippedFields）、LOGI_FIELDS のカタログ順。
 * - skippedFields の項目は列を持たないため全行が原則空欄——必須項目なら必須空欄エラーとして
 *   表示され、既存のインライン修正（1個ずつ手打ち）で埋められる。
 * - 各行: 項目値と形式エラー（日付・数値・必須空欄）。
 */
export const buildPreview = (
  table: EffectiveTable,
  mapping: ColumnMapping,
  overrides: CellOverrides = {},
  skippedFields: ReadonlySet<LogiFieldValue> = new Set(),
  masterCandidates: MasterCandidatesByField = {}
): PreviewResult => {
  const columnOf = new Map<LogiFieldValue, number>()
  for (const [key, field] of Object.entries(mapping)) {
    if (field !== null) columnOf.set(field, Number(key))
  }
  const fields = LOGI_FIELDS.filter(
    (f) => columnOf.has(f.value) || skippedFields.has(f.value)
  )

  const rows: PreviewDataRow[] = table.dataRows.map((cells, index) => {
    const rowNo = index + 1
    const rowOverrides = overrides[rowNo] ?? {}
    const revokeColumn = columnOf.get('revoke')
    // 取消の行は外部キー・振り分けキー・発送日（空なら着日）だけを見る（BE の標準パーサーと同じ）ので、
    // ほかの項目の必須・形式・マスタの照合はしない
    const isRevokedRow =
      (
        rowOverrides.revoke ??
        (revokeColumn !== undefined ? cells[revokeColumn] : '') ??
        ''
      ).trim() === REVOKE_MARK
    const record: Partial<Record<LogiFieldValue, string>> = {}
    const errors: PreviewCellError[] = []
    const editedFields: LogiFieldValue[] = []
    for (const field of fields) {
      const columnIndex = columnOf.get(field.value)
      const original =
        (columnIndex !== undefined ? cells[columnIndex] : '') ?? ''
      const overridden = rowOverrides[field.value]
      const raw = overridden ?? original
      if (overridden !== undefined) editedFields.push(field.value)
      record[field.value] = raw
      if (isRevokedRow && !REVOKED_ROW_CHECKED_FIELDS.has(field.value)) {
        continue
      }
      if (raw.trim() === '') {
        // 取消の行で空にできないのは外部キーだけ（発送日は空なら着日を使う）
        if (isRevokedRow ? field.value === 'externalKey' : field.required) {
          errors.push({
            field: field.value,
            reason:
              SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_REQUIRED_EMPTY
          })
        }
        continue
      }
      if (DATE_FIELDS.has(field.value) && !isParsableDate(raw)) {
        errors.push({
          field: field.value,
          reason: SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_DATE
        })
      } else if (NUMBER_FIELDS.has(field.value) && !isParsableNumber(raw)) {
        errors.push({
          field: field.value,
          reason: SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_NUMBER
        })
      } else if (
        MASTER_REFERENCE_FIELDS.has(field.value) &&
        !isKnownMasterCode(field.value, raw, masterCandidates)
      ) {
        errors.push({
          field: field.value,
          reason:
            SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ERROR_REASON_MASTER_NOT_FOUND
        })
      }
    }
    checkTimeRestrictionConstraints(record, fields, errors)
    return { rowNo, cells: record, errors, rowErrors: [], editedFields }
  })

  const errorCount = rows.filter((r) => r.errors.length > 0).length
  return {
    fields,
    rows,
    okCount: rows.length - errorCount,
    errorCount
  }
}
